import { z } from 'zod/v4';
import { stringToBooleanSchema } from '../common/index';

type AccessTargetType = 'COURSE' | 'MODULE' | 'MODULE_ITEM' | 'CHANNEL' | 'CONTENT';

const accessTargetTypes: [AccessTargetType, ...AccessTargetType[]] = [
  'COURSE', 'MODULE', 'MODULE_ITEM', 'CHANNEL', 'CONTENT',
];

const planTranslationSchema = z.object({
  languageCode: z.string()
    .min(2, 'Код языка должен быть минимум 2 символа')
    .max(5, 'Код языка должен быть максимум 5 символов'),
  name: z.string()
    .min(1, 'Название обязательно')
    .max(200, 'Название слишком длинное (максимум 200 символов)'),
  description: z.string().max(2000, 'Описание слишком длинное (максимум 2000 символов)').nullable().optional(),
  shortDescription: z.string().max(500, 'Краткое описание слишком длинное (максимум 500 символов)').nullable().optional(),
});

const entitlementSchema = z.object({
  targetType: z.enum(accessTargetTypes),
  targetId: z.number().int('ID цели должен быть целым числом').nullable().optional(),
  accessDurationDays: z.number()
    .int('Длительность доступа должна быть целым числом')
    .positive('Длительность доступа должна быть положительной')
    .nullable().optional(),
  isActive: z.boolean().default(true),
  order: z.number().int('Порядок должен быть целым числом').min(0, 'Порядок должен быть >= 0').default(0),
});

/**
 * Схема мультивалютных цен
 */
const multiCurrencyPricesSchema = z.record(
  z.string().min(3, 'Код валюты должен быть 3 символа').max(3, 'Код валюты должен быть 3 символа'),
  z.number().positive('Цена должна быть положительной'),
).nullable().optional();

export const createSubscriptionPlanSchema = z.object({
  code: z.string()
    .min(1, 'Код обязателен')
    .max(50, 'Код слишком длинный (максимум 50 символов)')
    .regex(/^[a-z0-9_-]+$/i, 'Код может содержать только буквы, цифры, дефис и подчёркивание'),
  price: z.number().nonnegative('Цена не может быть отрицательной').default(0),
  currency: z.string().length(3, 'Код валюты должен быть 3 символа').default('UZS'),
  // Мультивалютное ценообразование
  prices: multiCurrencyPricesSchema,
  baseCurrency: z.string()
    .min(3, 'Код базовой валюты должен быть 3 символа')
    .max(3, 'Код базовой валюты должен быть 3 символа')
    .default('UZS'),
  saleStartDate: z.string().pipe(z.iso.datetime()).nullable().optional(),
  saleEndDate: z.string().pipe(z.iso.datetime()).nullable().optional(),
  durationDays: z.number()
    .int('Длительность должна быть целым числом')
    .positive('Длительность должна быть положительной')
    .nullable().optional(),
  validUntil: z.string().pipe(z.iso.datetime()).nullable().optional(),
  isFamily: z.boolean().default(false),
  familySlots: z.number()
    .int('Количество мест должно быть целым числом')
    .min(1, 'Минимум 1 место')
    .default(1),
  isActive: z.boolean().default(true),
  imageId: z.number().int().positive().nullable().optional(),
  bannerImageId: z.number().int().positive().nullable().optional(),
  order: z.number().int('Порядок должен быть целым числом').min(0, 'Порядок должен быть >= 0').default(0),
  translations: z.array(planTranslationSchema).optional(),
  entitlements: z.array(entitlementSchema).optional(),
});

export const updateSubscriptionPlanSchema = createSubscriptionPlanSchema
  .omit({ code: true })
  .extend({ code: z.string().min(1).max(50).regex(/^[a-z0-9_-]+$/i).optional() })
  .partial();

export const subscriptionPlanFiltersSchema = z.object({
  isActive: stringToBooleanSchema,
  isFamily: stringToBooleanSchema,
  showDeleted: stringToBooleanSchema,
  search: z.string().optional(),
  sortBy: z.enum(['id', 'createdAt', 'updatedAt', 'code', 'price', 'order', 'isActive']).optional(),
  sortOrder: z.enum(['asc', 'desc']).optional(),
  page: z.coerce.number().int('Страница должна быть целым числом').positive('Страница должна быть положительной').default(1),
  limit: z.coerce.number().int('Лимит должен быть целым числом').positive('Лимит должен быть положительным').max(500, 'Лимит должен быть <= 500').default(20),
});

export const createEntitlementSchema = entitlementSchema;
export const updateEntitlementSchema = entitlementSchema.partial();

export type CreateSubscriptionPlanInput = z.infer<typeof createSubscriptionPlanSchema>;
export type UpdateSubscriptionPlanInput = z.infer<typeof updateSubscriptionPlanSchema>;
export type SubscriptionPlanFilters = z.infer<typeof subscriptionPlanFiltersSchema>;
export type CreateEntitlementInput = z.infer<typeof createEntitlementSchema>;
export type UpdateEntitlementInput = z.infer<typeof updateEntitlementSchema>;
